import { ChangeEvent, useEffect, useState } from 'react';
import { LABELS } from '~/resources/Labels';
import { useAppDispatch} from '~/hooks/appSelector';
import { FormScriptResponse, PropRow, SortTable } from '~/models/Commons ';
import { useDeleteScriptMutation, useGetScriptsQuery } from '~/services/script';
import { DELETE_SCRIPT_BYID} from '~/store/feature/Script';
import {
  attachResourcesToScripts,
  base64ToArrayBuffer,
  displayEntries,
  downloadBlob,
  handleTabChange,
  sortScripts,
} from '../../Utils/Utils';
import { ScriptsHeader } from '~/models/scripts/ScriptsHeader';
import { AddScript } from './AddScript';
import { SIMPLTable } from '../../components/commons/table/SimplTable';
import { Layout } from '../../components/commons/layout/Layout';
import { SIMPLTitle } from '../../components/commons/title/SIMPLTitle';
import AddIcon from '@mui/icons-material/Add';
import { SIMPLButtonCustomized } from '../../components/commons/button/SIMPLButtonCustumized';
import { DetailViewScript } from './DetailViewScript';
import { SIMPLAlert } from '../../components/commons/dialog/SimplAlert';
import { MESSAGES } from '~/resources/Message';
import { toast } from 'react-toastify';
import { SIMPLMenuHeader } from '../../components/commons/header/SIMPLMenuHeader';
import { PayloadType } from '~/models/types/Payload';
import { Entries } from '~/models/enums/Entries';
import { ConfigFile } from './ConfigFile';
import { LoadingWrapper } from '../../components/commons/spiner/SimpleLoaderWrapper';


export const Scripts = () => {
  const dispatch = useAppDispatch();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [active, setActive] = useState<boolean>(true);
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [rows, setRows] = useState([] as PropRow[]);
  const [openDetailView, setOpenDetailView] = useState(false);
  const [openConfigFile, setOpenConfigFile] = useState(false);
  const [item, setItem] = useState({} as FormScriptResponse);
  const [deleteModal, setDeleteModal] = useState(false);
  const [totalElements,setTotalElements] = useState<number>(0)
  const [deleteScript] = useDeleteScriptMutation();
  const [size,setSize] = useState<number>(5)
  const [page,setPage] = useState<number>(0)
  const [total, setTotal] = useState<number>(0);
  const [showPagination,setShowPagination] = useState(true)
  const { data,refetch,isLoading,isFetching,isError} = useGetScriptsQuery({ page, size,active });
  const [sortConfig, setSortConfig] = useState<SortTable>({
    key: '',
    direction: LABELS.ASC,
  });


  /**
 * Initializes and sets the table rows by:
 * 1. Filtering scripts based on the active tab.
 * 2. Sorting the filtered scripts according to the sort configuration.
 * 3. Mapping sorted scripts to table row format and setting rows.
 */
  const initScripts = () => {
    // Ensure payload exists before processing
    const payload = data?.payload as PayloadType | undefined;
  
    if (!payload || payload.length === 0) {
      setShowPagination(false);
      return; 
    }
    // Attach resources, sort scripts, and customize entries
    const scriptMergedList = attachResourcesToScripts(payload);
    const sortedScripts = sortScripts(scriptMergedList, sortConfig);
    const customizedList = displayEntries(sortedScripts, totalElements, Entries.Twenty);
    // Set pagination visibility and total elements count
    const totalPages = payload?.page?.totalPages ?? 0;
    setTotal(Number(totalPages));
  
    if (payload?.page?.totalElements) {
      setTotalElements(payload?.page?.totalElements);
      
    }
  
    // Process rows and set them
    const scriptRows: PropRow[] = customizedList.map((scriptItem: any) => ({
      row: scriptItem,
    }));

    setRows(scriptRows);
    setShowPagination(true); 
  };
  

  useEffect(() => {
      initScripts();
  }, [data, active, sortConfig,size,total,totalElements]);

  /** 
  * @effect
 * - Runs when the 'active' dependency changes.
 * - Calls setPage(1) to reset the pagination.
 */
  useEffect(() => {
    setPage(0);
    handlePaginationSize()
  }, [active,size]);
  const handlePaginationSize = () => {
    if ([Entries.Ten,Entries.Twenty].includes(size)) {
      setPage(0);
    }
  };
  const showFilterHandler = () => setShowFilter((prev) => !prev);
  const handleCloseAddModal = () => setIsAddModalOpen(false);
  const handleCloseConfigFileModal = () => setOpenConfigFile(false);
 
  const handleSizeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSize(Number(e.target.value));
  };
  const handleResetAll = ()=>{
    setPage(0)
    setShowFilter(false)
  }
  const handlePageChange = (page: number) => {
    setPage(page);
  };
 
  const handleSort = (key: string) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === LABELS.ASC
        ? LABELS.Desc
        : LABELS.ASC;
    setSortConfig({ key, direction });
  };

  /* Renders the "Add Script" button */
  const renderButtonAddScript = () => (
    <SIMPLButtonCustomized
      label={LABELS.AddScript}
      buttonHandlerAction={() => setIsAddModalOpen(true)}
      firstIcon={<AddIcon fontSize="small" />}
      secondIcon={<AddIcon fontSize="small" />}
      secondary
    />
  );
  /* Renders "Cancel" and "Delete Item" buttons for alerts */
  const renderAlertButtons = () => (
    <>
      <div className="grow-0 basis-32 mr-5">
        <SIMPLButtonCustomized
          label="Cancel"
          secondary
          buttonHandlerAction={() => setDeleteModal(false)}
        />
      </div>
      <div className="grow-0 basis-32">
        <SIMPLButtonCustomized
          label="Delete Item"
          danger
          buttonHandlerAction={handleDeleteItem}
        />
      </div>
    </>
  );
  
/*Renders delete alert with cancel and delete actions */
  const renderDeleteAlert = () =>
    deleteModal && (
      <SIMPLAlert
        onCancel={() => setDeleteModal(false)}
        message={MESSAGES.MessageDialogDelete(item.title)}
        title="Delete Script"
        actions={renderAlertButtons()}
      />
    );

 

  const handleConfirmDelete = (scriptItem: FormScriptResponse) => {
    if (scriptItem.scriptIdentify.deploymentScriptId) {
      setItem(scriptItem);
      setDeleteModal(true);
    } else {
      console.warn(LABELS.ScriptIdDonotExist);
    }
  };

  const handleDeleteItem = async () => {
    try {
      await toast.promise(
        async () => {
          const { success } = await deleteScript(item.scriptIdentify.deploymentScriptId).unwrap();
          dispatch(DELETE_SCRIPT_BYID(item.scriptIdentify.deploymentScriptId));
          return success
        },
        {
          pending: 'Deleting Script...',
          success: MESSAGES.MessageSucessFullyRemoved,
          error: MESSAGES.ErrorDelectingScript,
        }
      );
    } catch (error: any) {
      const errorMessageTitle =
        error?.data?.title || error?.title || MESSAGES.ErrorDelectingScript;
      const errorMessageStstus =
        error?.data?.status ||
        error?.status ||
        MESSAGES.ErrorDelectingScript;
      toast.error(`Status:${errorMessageStstus}\nTitle:${errorMessageTitle}`);
      console.error("Error", error);
    } finally {
      setDeleteModal(false);
      refetch()
    }
  };
 
  const renderDetailViewScript = () =>
    openDetailView && (
      <DetailViewScript item={item} onClose={() => setOpenDetailView(false)} />
    );
    const renderConfigFile = () =>
      openConfigFile && (
        <ConfigFile item={item} 
        onCloseModal={handleCloseConfigFileModal} 
        editedItem={item}
         setConfig={setSortConfig} 
        refetch={refetch} />
      );     
  const renderSIMPLTitle = () => (
    <SIMPLTitle title={LABELS.ScriptManagmentTitle} />
  );

  const renderAddScriptModal = () =>
    isAddModalOpen && (
      <AddScript onCloseModal={handleCloseAddModal} editedItem={null}
       setConfig={setSortConfig}
       title={LABELS.AddScript}
       refetch={refetch}/>
    );


    /* Render table and actions for view,delete and download*/
  const renderSIMPLTable = () => (
    <div className="flex flex-col w-full">
      <div className="mt-5">
        <div className="container mx-auto mt-10">
          <SIMPLTable
          custumizedNoDataAvailable={LABELS.ScriptNoDataAvailable}
          clear={handleResetAll}
          page={page}
          total={total}
          totalElements={totalElements}
          showPagination={showPagination}
            active={active}
            showHandler={showFilterHandler}
            filterActive={showFilter}
            tableHeaders={ScriptsHeader}
            rows={rows}
            onChangePage={handlePageChange}
            onChange={handleSizeChange}
            button={renderButtonAddScript()}
            sortConfig={sortConfig}
            handleSort={handleSort}
            chipLabels={[]}
            handleOpenDetail={handleOpenDetail}
            handleConfigFile={handleConfigFile}
            handleRowClick={handleRowClick}
            handleDownload={handleDownload}
            handleDelete={handleConfirmDelete}
            showButtonDetails
            showButtonDownload
            showButtonDelete
            showConfigFile
            handlerChips={() =>
              handleTabChange(active, LABELS.Active, LABELS.Inactive, setActive)
            }
          />
        </div>
      </div>
    </div>
  );

  const handleRowClick = (scriptItem: FormScriptResponse) => {
    if (!scriptItem.active) {
      setItem(scriptItem);
      setOpenDetailView(true);
    }
  };

  const handleOpenDetail = (scriptItem: FormScriptResponse) => {
    if (scriptItem.active) {
      setItem(scriptItem);
      setOpenDetailView(true);
    } else {
      console.warn(LABELS.ScriptIdDonotExist);
    }
  };
  const handleConfigFile = (scriptItem: FormScriptResponse) => {
    if (scriptItem.active) {
      setItem(scriptItem);
      setOpenConfigFile(true);
    } else {
      console.warn(LABELS.ScriptIdDonotExist);
    }
  };

  const handleDownload = (scriptItem: FormScriptResponse) => {
    const arrayBuffer = base64ToArrayBuffer(scriptItem.file);
    downloadBlob(arrayBuffer, `${scriptItem.originalFileName}`);
  };

  return (
    <SIMPLMenuHeader>  
    <Layout>
      {renderDeleteAlert()}
      {renderDetailViewScript()}
      {renderConfigFile()}
      {renderSIMPLTitle()}
      {renderAddScriptModal()}
      <LoadingWrapper
      loadingTitle='Loading scripts...' 
      isLoading={isLoading || isFetching} 
      isError={isError}>
      {renderSIMPLTable()}
      </LoadingWrapper>
    </Layout>
    </SIMPLMenuHeader>  
  );
};
